import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { mapboxAccessToken } from "../map/config";
import {
    useKretsMembers,
    useCreateKretsMember,
    useUpdateKretsMember,
    useDeleteKretsMember,
    type KretsMember,
} from "../api/kretsMembers";
import { fetchUsers } from "../api/users";
import { useTeams, useTeamMunicipalities, normalizeTeamTypeTier } from "../api/teams";
import { useSelectedMunicipalityId } from "./selectedMunicipality";
import "./krets-members-panel.css";

interface KretsMemberFormState {
    name: string;
    teamId: string;
    homeAddress: string;
    homeLatitude: number | null;
    homeLongitude: number | null;
    phoneNumber: string;
    userId: string;
}

const emptyForm = (): KretsMemberFormState => ({
    name: "",
    teamId: "",
    homeAddress: "",
    homeLatitude: null,
    homeLongitude: null,
    phoneNumber: "",
    userId: "",
});

const memberToForm = (m: KretsMember): KretsMemberFormState => ({
    name: m.name,
    teamId: m.teamId,
    homeAddress: m.homeAddress ?? "",
    homeLatitude: m.homeLatitude ?? null,
    homeLongitude: m.homeLongitude ?? null,
    phoneNumber: m.phoneNumber ?? "",
    userId: m.userId ?? "",
});

interface GeocodeSuggestion {
    id: string;
    place_name: string;
    center: [number, number];
}

/** Öppnar platsen i Google Maps (koordinater prioriteras framför fritextadress). */
const googleMapsSearchUrl = (m: KretsMember): string | null => {
    if (m.homeLatitude != null && m.homeLongitude != null) {
        return `https://www.google.com/maps/search/?api=1&query=${m.homeLatitude},${m.homeLongitude}`;
    }
    const addr = m.homeAddress?.trim();
    if (addr) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
    }
    return null;
};

const searchAddress = async (query: string, token: string): Promise<GeocodeSuggestion[]> => {
    if (!query || query.length < 3) return [];
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=se&types=address&limit=5&access_token=${token}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.features ?? []).map((f: any) => ({
        id: f.id,
        place_name: f.place_name,
        center: f.center as [number, number],
    }));
};

export const KretsMembersPanel = (): JSX.Element => {
    const [editing, setEditing] = useState<KretsMember | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<KretsMemberFormState>(emptyForm());
    const [addressQuery, setAddressQuery] = useState("");
    const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [teamFilter, setTeamFilter] = useState("");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipNextAddressSearchRef = useRef(false);
    const mapToken = mapboxAccessToken;

    const { data: members = [], isLoading } = useKretsMembers();
    const { data: users = [] } = useQuery({
        queryKey: ["users"],
        queryFn: () => fetchUsers(),
        staleTime: 5 * 60 * 1000,
    });
    const { data: allTeams = [] } = useTeams();
    const { data: teamMunicipalities = [] } = useTeamMunicipalities();
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useSelectedMunicipalityId();

    const createMutation = useCreateKretsMember();
    const updateMutation = useUpdateKretsMember();
    const deleteMutation = useDeleteKretsMember();

    // Krets teams from the teams API (type 2 = Krets)
    const teams = allTeams
        .filter(t => normalizeTeamTypeTier(t.type) === 2)
        .map(t => ({ id: t.id, name: t.name }))
        .sort((a, b) => a.name.localeCompare(b.name, "sv"));

    const displayed = teamFilter ? members.filter(m => m.teamId === teamFilter) : [];

    // Kretsen väljs här, men kommunen är det som delas med Uppdrag-sidorna. Saknas kopplingen
    // (äldre API eller krets utan kommun) faller sidan tillbaka på ett lokalt kretsval.
    const teamIdByMunicipalityId = useMemo(
        () => new Map(teamMunicipalities.map(tm => [tm.municipalityPolygonId, tm.teamId])),
        [teamMunicipalities]
    );
    const municipalityIdByTeamId = useMemo(
        () => new Map(teamMunicipalities.map(tm => [tm.teamId, tm.municipalityPolygonId])),
        [teamMunicipalities]
    );

    useEffect(() => {
        if (!selectedMunicipalityId) return;
        const mappedTeamId = teamIdByMunicipalityId.get(selectedMunicipalityId);
        if (mappedTeamId) {
            setTeamFilter(mappedTeamId);
        }
    }, [selectedMunicipalityId, teamIdByMunicipalityId]);

    const handleTeamFilterChange = (nextTeamId: string) => {
        setTeamFilter(nextTeamId);
        setSelectedMunicipalityId(nextTeamId ? municipalityIdByTeamId.get(nextTeamId) ?? "" : "");
    };

    useEffect(() => {
        if (skipNextAddressSearchRef.current) {
            skipNextAddressSearchRef.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (addressQuery.length < 3) { setSuggestions([]); return; }
        debounceRef.current = setTimeout(async () => {
            const results = await searchAddress(addressQuery, mapToken);
            setSuggestions(results);
            setShowSuggestions(results.length > 0);
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [addressQuery, mapToken]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm(), teamId: teamFilter });
        setAddressQuery("");
        setSuggestions([]);
        setShowSuggestions(false);
        setShowForm(true);
    };

    const openEdit = (m: KretsMember) => {
        setEditing(m);
        setForm(memberToForm(m));
        setAddressQuery(m.homeAddress ?? "");
        setSuggestions([]);
        setShowSuggestions(false);
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditing(null);
    };

    const selectSuggestion = (s: GeocodeSuggestion) => {
        setForm(prev => ({
            ...prev,
            homeAddress: s.place_name,
            homeLongitude: s.center[0],
            homeLatitude: s.center[1],
        }));
        skipNextAddressSearchRef.current = true;
        setAddressQuery(s.place_name);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editing) {
            const req: any = { id: editing.id, name: form.name };
            if (form.homeLatitude !== null && form.homeLongitude !== null) {
                req.homeLatitude = form.homeLatitude;
                req.homeLongitude = form.homeLongitude;
                req.homeAddress = form.homeAddress || undefined;
            } else if (!form.homeAddress) {
                req.clearHomeLocation = true;
            }
            // Always sent: an empty string clears the number server-side.
            req.phoneNumber = form.phoneNumber.trim();
            req.userId = form.userId || undefined;
            req.clearUserId = !form.userId && !!editing.userId;
            updateMutation.mutate(req, { onSuccess: closeForm });
        } else {
            createMutation.mutate(
                {
                    name: form.name,
                    teamId: form.teamId,
                    homeLatitude: form.homeLatitude ?? undefined,
                    homeLongitude: form.homeLongitude ?? undefined,
                    homeAddress: form.homeAddress || undefined,
                    phoneNumber: form.phoneNumber.trim() || undefined,
                    userId: form.userId || undefined,
                },
                { onSuccess: closeForm }
            );
        }
    };

    const handleDelete = (m: KretsMember) => {
        if (!confirm(`Ta bort ${m.name}?`)) return;
        deleteMutation.mutate(m.id);
    };

    const clearLocation = () => {
        setForm(prev => ({ ...prev, homeLatitude: null, homeLongitude: null, homeAddress: "" }));
        setShowSuggestions(false);
        setSuggestions([]);
        setAddressQuery("");
    };

    return (
        <div className="krets-members-panel">
            <div className="krets-members-panel__header">
                <h2>Utdelare</h2>
            </div>

            <div className="krets-members-panel__filter">
                <label htmlFor="krets-members-team-filter" className="krets-members-panel__filter-label">
                    Kommun
                </label>
                <select
                    id="krets-members-team-filter"
                    value={teamFilter}
                    onChange={e => handleTeamFilterChange(e.target.value)}
                >
                    <option value="">Välj kommun</option>
                    {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
                {teamFilter && (
                    <button className="krets-members-panel__add-btn" onClick={openCreate}>
                        + Lägg till
                    </button>
                )}
            </div>

            {!teamFilter && (
                <p className="krets-members-panel__empty">Välj kommun för att visa och lägga till utdelare.</p>
            )}

            {showForm && (
                <div className="krets-members-panel__form-overlay">
                    <form className="krets-members-panel__form" onSubmit={handleSubmit}>
                        <h3>{editing ? "Redigera utdelare" : "Lägg till utdelare"}</h3>

                        <label>
                            Namn
                            <input
                                type="text"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                required
                                maxLength={200}
                                placeholder="För- och efternamn"
                            />
                        </label>

                        <label>
                            Hemadress
                            <div className="krets-members-panel__address-wrap">
                                <input
                                    type="text"
                                    value={addressQuery}
                                    onChange={e => {
                                        setAddressQuery(e.target.value);
                                        setShowSuggestions(true);
                                        if (!e.target.value) clearLocation();
                                    }}
                                    placeholder="Sök adress…"
                                    autoComplete="off"
                                />
                                {(form.homeLatitude !== null) && (
                                    <button type="button" className="krets-members-panel__clear-loc" onClick={clearLocation}>✕</button>
                                )}
                            </div>
                            {showSuggestions && suggestions.length > 0 && (
                                <ul className="krets-members-panel__suggestions">
                                    {suggestions.map(s => (
                                        <li key={s.id} onClick={() => selectSuggestion(s)}>
                                            {s.place_name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {form.homeLatitude !== null && (
                                <small className="krets-members-panel__coords">
                                    {form.homeLatitude.toFixed(5)}, {form.homeLongitude?.toFixed(5)}
                                </small>
                            )}
                        </label>

                        <label>
                            Telefonnummer (valfritt)
                            <input
                                type="tel"
                                value={form.phoneNumber}
                                onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value }))}
                                maxLength={50}
                                placeholder="t.ex. 070-123 45 67"
                                autoComplete="off"
                            />
                        </label>

                        <label>
                            Länka till användarkonto (valfritt)
                            <select
                                value={form.userId}
                                onChange={e => setForm(p => ({ ...p, userId: e.target.value }))}
                            >
                                <option value="">Inget konto</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.userName}</option>
                                ))}
                            </select>
                        </label>

                        <div className="krets-members-panel__form-actions">
                            <button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                                {editing ? "Spara" : "Lägg till"}
                            </button>
                            <button type="button" onClick={closeForm}>Avbryt</button>
                        </div>

                        {(createMutation.isError || updateMutation.isError) && (
                            <p className="krets-members-panel__error">Något gick fel. Försök igen.</p>
                        )}
                    </form>
                </div>
            )}

            {isLoading ? (
                <p className="krets-members-panel__loading">Laddar…</p>
            ) : !teamFilter ? null : displayed.length === 0 ? (
                <p className="krets-members-panel__empty">Inga utdelare hittades.</p>
            ) : (
                <table className="krets-members-panel__table">
                    <thead>
                        <tr>
                            <th>Namn</th>
                            <th>Krets</th>
                            <th>Hemadress</th>
                            <th>Telefon</th>
                            <th>Konto</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayed.map(m => {
                            const mapsUrl = googleMapsSearchUrl(m);
                            const addressLabel = m.homeAddress?.trim() || (mapsUrl ? "Visa på kartan" : null);
                            return (
                            <tr key={m.id}>
                                <td>{m.name}</td>
                                <td>{m.teamName}</td>
                                <td className={addressLabel ? "" : "krets-members-panel__no-addr"}>
                                    {mapsUrl && addressLabel ? (
                                        <a
                                            href={mapsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="krets-members-panel__maps-link"
                                        >
                                            {addressLabel}
                                        </a>
                                    ) : (
                                        "–"
                                    )}
                                </td>
                                <td className={m.phoneNumber ? "" : "krets-members-panel__no-addr"}>
                                    {m.phoneNumber ? (
                                        <a
                                            href={`tel:${m.phoneNumber.replace(/[^\d+]/g, "")}`}
                                            className="krets-members-panel__phone-link"
                                        >
                                            {m.phoneNumber}
                                        </a>
                                    ) : (
                                        "–"
                                    )}
                                </td>
                                <td>{m.userName ?? "–"}</td>
                                <td className="krets-members-panel__actions">
                                    <button onClick={() => openEdit(m)}>Redigera</button>
                                    <button
                                        className="krets-members-panel__delete-btn"
                                        onClick={() => handleDelete(m)}
                                        disabled={deleteMutation.isPending}
                                    >
                                        Ta bort
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};
